import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const navigate = useNavigate();
  const { fetchCartCount } = useContext(AuthContext);
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        withCredentials: true
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Error al cargar el carrito');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    try {
      await axios.post(`${API}/cart/update?product_id=${productId}&quantity=${newQuantity}`, {}, {
        withCredentials: true
      });
      await fetchCart();
      fetchCartCount();
      toast.success('Carrito actualizado');
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error('Error al actualizar el carrito');
    }
  };

  const removeItem = async (productId) => {
    await updateQuantity(productId, 0);
  };

  const calculateTotal = () => {
    return cart.items.reduce((sum, item) => {
      const finalPrice = item.product.discount_percentage > 0 
        ? item.product.price * (1 - item.product.discount_percentage / 100)
        : item.product.price;
      return sum + (finalPrice * item.quantity);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-purple-400 text-xl">Cargando carrito...</div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" data-testid="empty-cart">
        <ShoppingBag size={80} className="text-gray-600 mb-6" />
        <h2 className="text-2xl font-bold text-gray-400 mb-4">Tu carrito está vacío</h2>
        <Button
          onClick={() => navigate('/shop')}
          className="btn-primary bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="go-shopping-button"
        >
          Ir de Compras
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="cart-page">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-testid="cart-title">
          Carrito de Compras
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div key={item.product.id} className="glass rounded-xl p-6 fade-in" data-testid="cart-item">
                <div className="flex gap-6">
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="w-32 h-32 object-cover rounded-lg"
                    data-testid="cart-item-image"
                  />
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2" data-testid="cart-item-name">{item.product.name}</h3>
                    <p className="text-gray-400 text-sm mb-4" data-testid="cart-item-category">{item.product.category}</p>
                    
                    <div className="flex items-center justify-between">
                      <div data-testid="cart-item-price">
                        {item.product.discount_percentage > 0 ? (
                          <div>
                            <div className="text-gray-500 line-through text-sm">{formatCurrency(item.product.price)}</div>
                            <div className="text-2xl font-bold text-purple-400">
                              {formatCurrency(item.product.price * (1 - item.product.discount_percentage / 100))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-2xl font-bold text-purple-400">
                            {formatCurrency(item.product.price)}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                          data-testid="decrease-cart-quantity"
                        >
                          <Minus size={16} />
                        </Button>
                        
                        <span className="text-white font-bold w-8 text-center" data-testid="cart-item-quantity">{item.quantity}</span>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                          data-testid="increase-cart-quantity"
                        >
                          <Plus size={16} />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.product.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          data-testid="remove-cart-item"
                        >
                          <Trash2 size={20} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass rounded-xl p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-white mb-6" data-testid="order-summary-title">Resumen del Pedido</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal</span>
                  <span data-testid="subtotal">{formatCurrency(calculateTotal())}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Envío</span>
                  <span className="text-green-400">GRATIS</span>
                </div>
                <div className="border-t border-purple-900/50 pt-4 flex justify-between text-xl font-bold text-white">
                  <span>Total</span>
                  <span data-testid="total">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
              
              <Button
                onClick={() => navigate('/checkout')}
                className="w-full btn-primary bg-purple-600 hover:bg-purple-700 text-white text-lg py-6 rounded-full"
                data-testid="checkout-button"
              >
                Proceder al Pago
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;